import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PropsWithChildren, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ShellLayout } from './components/layout/ShellLayout';
import { AuthCallbackPage } from './features/auth/AuthCallbackPage';
import { LoginPage } from './features/auth/LoginPage';
import { RegisterPage } from './features/auth/RegisterPage';
import { ForgotPasswordPage } from './features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './features/auth/ResetPasswordPage';
import { LauncherPage } from './features/launcher/LauncherPage';
import { RemoteAppHost } from './features/launcher/RemoteAppHost';
import { IdentityBootstrap } from './features/identity/IdentityBootstrap';
import { ProfilePage } from './features/settings/ProfilePage';
import { ShellHostContextBridge } from './infrastructure/shell-runtime/ShellHostContextBridge';
import { useAuthStore } from './state/auth-store';

// Crear instancia del QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function RequireAuth({ children }: PropsWithChildren) {
  const status = useAuthStore((state) => state.status);
  return status === 'authenticated' ? <>{children}</> : <Navigate to="/login" replace />;
}

function RedirectIfAuthenticated({ children }: PropsWithChildren) {
  const status = useAuthStore((state) => state.status);
  return status === 'authenticated' ? <Navigate to="/" replace /> : <>{children}</>;
}

function AuthBootstrap() {
  const status = useAuthStore((state) => state.status);
  const refreshSession = useAuthStore((state) => state.refreshSession);

  useEffect(() => {
    if (status === 'authenticated') {
      void refreshSession();
    }
  }, [status, refreshSession]);

  return null;
}

export function App() {
  const status = useAuthStore((state) => state.status);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthBootstrap />
      {status === 'authenticated' ? <IdentityBootstrap /> : null}
      <BrowserRouter>
        <ShellHostContextBridge />
        <Routes>
          <Route
            path="/login"
            element={
              <RedirectIfAuthenticated>
                <LoginPage />
              </RedirectIfAuthenticated>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <RedirectIfAuthenticated>
                <ForgotPasswordPage />
              </RedirectIfAuthenticated>
            }
          />
          <Route
            path="/reset-password"
            element={
              <RedirectIfAuthenticated>
                <ResetPasswordPage />
              </RedirectIfAuthenticated>
            }
          />
          <Route
            path="/register"
            element={
              <RedirectIfAuthenticated>
                <RegisterPage />
              </RedirectIfAuthenticated>
            }
          />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route
            path="/"
            element={
              <RequireAuth>
                <ShellLayout />
              </RequireAuth>
            }
          >
            <Route index element={<LauncherPage />} />
            <Route path="settings/profile" element={<ProfilePage />} />
            <Route path="apps/:appKey/*" element={<RemoteAppHost />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
